import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";

import { IconButton } from "@/components/ui/icon-button";
import { ViewOnlyIndicator } from "@/features/onboarding/view-only-indicator";
import { cn } from "@/lib/utils";

export interface ViewerBackButtonProps {
  /** "Back to {Destination}" — the accessible name and tooltip. */
  label: string;
  onBack: () => void;
  className?: string;
}

/** The exit control for full-screen viewers. Always an ArrowLeft, never an X. */
export function ViewerBackButton({ label, onBack, className }: ViewerBackButtonProps) {
  return (
    <IconButton
      label={label}
      icon={ArrowLeft}
      size="sm"
      tooltipSide="bottom"
      className={cn("shrink-0", className)}
      onClick={onBack}
    />
  );
}

export interface ViewerTopBarProps {
  /** Usually `<ViewerBackButton … />`. */
  back: ReactNode;
  /** Job / document name. */
  title: string;
  /** Secondary line under the title (revision, schedule name). */
  subtitle?: ReactNode;
  /** Shows the shared `ViewOnlyIndicator` (it renders only for view-only viewers). */
  readOnly?: boolean;
  /** Document/drawing select + pager. */
  source?: ReactNode;
  /** `ViewerMetaChip`s and similar context after the source controls. */
  meta?: ReactNode;
  /** View/display toggles, undo/redo, and tools — scrolls horizontally when space runs out. */
  tools?: ReactNode;
  /** Trailing actions; Export goes last. */
  actions?: ReactNode;
  className?: string;
}

/**
 * The single `h-12` toolbar row for full-screen viewers (TakeoffPilot, FieldPilot, RevCollab).
 * Left: back, title block, view-only marker, divider, source, meta. Right: tools strip, actions.
 * The caller positions it (absolute overlay over the canvas, or in flow).
 */
export function ViewerTopBar({
  back,
  title,
  subtitle,
  readOnly = false,
  source,
  meta,
  tools,
  actions,
  className,
}: ViewerTopBarProps) {
  return (
    <div
      data-slot="viewer-top-bar"
      className={cn(
        "flex h-12 w-full min-w-0 items-center gap-2 border-b border-border bg-background/95 px-3 backdrop-blur",
        className
      )}
    >
      {back}
      <div className="min-w-0 max-w-56 shrink">
        <h1 className="truncate text-sm font-semibold leading-tight" title={title}>
          {title}
        </h1>
        {subtitle ? (
          <p className="truncate text-xs leading-tight text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {readOnly ? <ViewOnlyIndicator /> : null}
      {source || meta ? (
        <div aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-border" />
      ) : null}
      {source ? <div className="flex shrink-0 items-center gap-2">{source}</div> : null}
      {meta ? <div className="flex min-w-0 shrink items-center gap-2">{meta}</div> : null}
      {tools || actions ? (
        <div className="ms-auto flex min-w-0 items-center gap-2">
          {tools ? (
            // py-1/-my-1 leaves room for focus rings inside the scroll container.
            <div
              data-slot="viewer-top-bar-tools"
              className="-my-1 flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden py-1"
            >
              {tools}
            </div>
          ) : null}
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

/*
 * Usage Example:
 *
 * <div className="pointer-events-none absolute inset-x-0 top-0 z-[60]">
 *   <ViewerTopBar
 *     className="pointer-events-auto"
 *     back={<ViewerBackButton label="Back to FieldPilot" onBack={exit} />}
 *     title={job.name}
 *     subtitle={job.scheduleRevisionName ?? "No schedule"}
 *     readOnly={!canEdit}
 *     source={<DrawingSelect … />}
 *     meta={<ViewerMetaChip>Page 2 of 14</ViewerMetaChip>}
 *     tools={<ToolButtons … />}
 *     actions={<ExportButton … />}
 *   />
 * </div>
 */
